import { useEffect, useState } from "react";
import { useApiClient } from "../../common/hooks/useApiClient";
import { TagFormat } from "../../common/models/schema/TagFormat";
import { ApiError } from "../../common/api/ApiClient";

export const useTagsManager = () => {
  const [tags, setTags] = useState<TagFormat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const apiClient = useApiClient();
  const basePath = "/tags";

  useEffect(() => {
    getTags();
  }, []);

  const getTags = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<TagFormat[]>(basePath);
      setTags(response.data);
    } catch (error) {
      const apiError = error as ApiError;
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const createTag = async (tag: TagFormat) => {
    try {
      setLoading(true);
      const newTag = await apiClient.post<TagFormat>(basePath, tag);
      setTags([...tags, newTag.data]);
    } catch (error) {
      const apiError = error as ApiError;
      setCreateError(apiError.data?.detail.message || apiError.message);
      throw apiError;
    } finally {
      setLoading(false);
    }
  };

  const updateTag = async (tag: TagFormat) => {
    try {
      setLoading(true);
      const updatedTag = await apiClient.put<TagFormat>(
        basePath + "/" + tag.id,
        tag,
      );
      setTags(tags.map((t) => (t.id === tag.id ? updatedTag.data : t)));
    } catch (error) {
      const apiError = error as ApiError;
      setUpdateError(apiError.data?.detail.message || apiError.message);
      throw apiError;
    } finally {
      setLoading(false);
    }
  };

  const deleteTag = async (id: number) => {
    try {
      setLoading(true);
      await apiClient.delete<TagFormat>(basePath + "/" + id);
      setTags(tags.filter((t) => t.id !== id));
    } catch (error) {
      const apiError = error as ApiError;
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    tags,
    loading,
    error,
    createError,
    updateError,
    getTags,
    createTag,
    updateTag,
    deleteTag,
  };
};
